import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";

interface TypewriterTextProps {
    text: string;
    active: boolean;
    className?: string;
    speed?: number;
    onComplete?: () => void;
}

export const TypewriterText: React.FC<TypewriterTextProps> = ({
    text,
    active,
    className,
    speed = 20,
    onComplete,
}) => {
    const [displayCount, setDisplayCount] = useState(0);
    const [done, setDone] = useState(false);

    const prevActiveRef = useRef(active);
    const prevTextRef = useRef(text);

    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    // Refs for rAF-based animation (no re-renders between frames)
    const countRef = useRef(0);
    const rafRef = useRef(0);
    const lastTimeRef = useRef(0);
    const accRef = useRef(0);

    useEffect(() => {
        const becameActive = !prevActiveRef.current && active;
        const textChanged = prevTextRef.current !== text;

        if (becameActive || textChanged) {
            prevActiveRef.current = active;
            prevTextRef.current = text;
            countRef.current = 0;
            accRef.current = 0;
            lastTimeRef.current = 0;
            setDisplayCount(0);
            setDone(false);
        } else {
            prevActiveRef.current = active;
        }
    }, [active, text]);

    useEffect(() => {
        if (!active || done || !text) return;

        const step = (now: number) => {
            if (lastTimeRef.current === 0) {
                lastTimeRef.current = now;
            }

            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;
            accRef.current += delta;

            const chars = Math.floor(accRef.current / speed);
            if (chars > 0) {
                accRef.current -= chars * speed;
                const next = Math.min(
                    countRef.current + chars,
                    text.length
                );
                countRef.current = next;
                setDisplayCount(next);

                if (next >= text.length) {
                    setDone(true);
                    onCompleteRef.current?.();
                    return;
                }
            }

            rafRef.current = requestAnimationFrame(step);
        };

        rafRef.current = requestAnimationFrame(step);

        return () => {
            cancelAnimationFrame(rafRef.current);
            lastTimeRef.current = 0;
            accRef.current = 0;
        };
    }, [active, text, done, speed]);

    const visibleText =
        done || displayCount > 0
            ? text.slice(text.length - displayCount)
            : "";

    return (
        <span
            className={classNames("tw", className, {
                "tw--done": done,
            })}
        >
            {/* Kursor: tylko gdy animacja trwa (active && !done) */}
            {active && !done && (
                <span className="tw__cursor" aria-hidden="true" />
            )}

            {/* Tekst wychodzący "z kursora" */}
            <span className="tw__text">{visibleText}</span>
        </span>
    );
};
